import {
  CSSProperties,
  LegacyRef,
  MutableRefObject,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
  useLayoutEffect,
  MouseEvent,
  PointerEvent,
} from 'react';

import {
  EMPTY_POSITIONS,
  FILES_BLACK,
  FILES_WHITE,
  RANKS_BLACK,
  RANKS_WHITE,
  SQUARES,
  SQUARES_REVERSE,
} from '@libs/chess/constants';
import { parseFen } from '@libs/chess/parseFen';
import {
  BoardPositions,
  ChessWinner,
  Color,
  PieceCode,
  PieceName,
  PromotionPiece,
  RankName,
  SquareName,
} from '@libs/chess/types';
import { cx } from '@libs/classnames';
import { FUNC_PREVENT_DEFAULT } from '@libs/constants';
import { useDrag, useDrop } from '@libs/hooks/useDragAndDrop';
import { useIsRendered } from '@libs/hooks/useIsRendered';
import { usePrev } from '@libs/hooks/usePrev';
import { useResizeObserver } from '@libs/hooks/useResizeObserver';
import { AnimatedDynamicNodes, AnimatedNode, NodeInfo } from '@ui/components/Animation';
import { BoardArrow } from '@ui/components/BoardArrow';
import { BoardStockfishArrow } from '@ui/components/BoardStockfishArrow';
import { RotateBoardButton } from '@ui/components/RotateBoardButton/RotateBoardButton';

import { DefaultChessPieces } from './ChessPieces';
import { ChessPieces } from './ChessPieces/types';
import { PromotionSelector } from './PromotionSelector/PromotionSelector';
import { BoardThemes, BOARD_THEMES } from './themes';
import { DefaultTokens } from './Tokens';
import { TokenIcons, TokenType } from './Tokens/types';
import { getPromotionPosition } from './utils/promotionPosition';
import { BoardPositionsIds, trackIds } from './utils/trackIds';

import styles from './ChessBoard.module.css';

/*
    ____,-------------------------------,____
    \   |          Компонент            |   /
    /___|-------------------------------|___\
*/

export type Props = {
  className?: string;
  theme?: BoardThemes;

  orientation: 'white' | 'black';
  position?: string | BoardPositions;
  notation?: 'outside' | 'inside' | 'auto' | 'none';
  noAnimation?: boolean;
  piecesAnimation?: AnimationType;
  noShadow?: boolean;
  noAutoBorderRadius?: boolean;
  noBorderRadius?: boolean;
  noPieces?: boolean;
  noWaypoints?: boolean;

  modal?: ReactNode;

  interactive?: 'none' | 'white' | 'black' | 'all';

  valuation?: 'none' | 'show' | 'hide';
  valuationValue?: Valuation;

  highlightHovered?: boolean;

  onPieceDrag?: (from: SquareName, positions: BoardPositions) => boolean;

  onSquareClick?: (s: SquareName, positions: BoardPositions, e: MouseEvent) => void;
  onSquarePointerDown?: (s: SquareName, positions: BoardPositions, e: PointerEvent) => void;
  onSquarePointerUp?: (s: SquareName, positions: BoardPositions, e: PointerEvent) => void;
  onSquareOver?: (s: SquareName, positions: BoardPositions) => void;
  onSquareOut?: (s: SquareName, positions: BoardPositions) => void;
  onSquareDragOver?: (s: SquareName, positions: BoardPositions, e: globalThis.PointerEvent) => void;
  onSquareDrop?: (from: SquareName, to: SquareName, positions: BoardPositions) => void;
  onDropOutside?: (from: SquareName, position: BoardPositions) => void;
  onDragAbort?: (from: SquareName, position: BoardPositions) => void;
  onSquarePlace?: (to: SquareName, piece: PieceCode, positions: BoardPositions) => void;
  onTokenPlace?: (to: SquareName, token: TokenType, positions: BoardPositions) => void;
  onTokenDrop?: (from: SquareName, to: SquareName, positions: BoardPositions) => void;
  onTokenDropOutside?: (from: SquareName, positions: BoardPositions) => void;
  onKeyDown?: (e: KeyboardEvent) => void;

  isPremove?: boolean;
  selectedFrom?: SquareName;
  selectedTo?: SquareName;
  premoveFrom?: SquareName;
  premoveTo?: SquareName;
  moves?: SquareName[];

  arrows?: Arrow[];
  onArrowDrag?: (from: SquareName) => boolean;
  onArrowPlace?: (arrow: UserArrow) => void;

  // Фишки головоломок: звездочки, стены, яблоки
  tokens?: Token[];
  tokensAnimation?: AnimationType;

  winner?: ChessWinner;
  promotingFrom?: SquareName;
  promotingTo?: SquareName;
  onPromotion?: (promoteTo: PromotionPiece) => void;

  onRotate?: (nextOrientation: 'black' | 'white') => void;
};

type AnimationType = 'opacity' | 'scale' | 'none';

type DragInfo = {
  square: SquareName;
  pieceCode: PieceCode;
  pieceId: string;
};
type DragMoveData = {
  type: 'move';
  square: SquareName;
  ac: AbortController;
  draggedFromSelf: boolean;
};
type DragArrowData = {
  type: 'arrow';
  color: 'green' | 'red' | 'yellow' | 'blue';
  square: SquareName;
  ac: AbortController;
  draggedFromSelf: boolean;
};
type DragTokenData = {
  type: 'move-token';
  square: SquareName;
  token: TokenType;
  ac: AbortController;
  draggedFromSelf: boolean;
};
type TokenDragInfo = { square: SquareName; token: TokenType };
export type DragPlaceData =
  | { type: 'place'; piece: PieceCode }
  | { type: 'place-token'; token: TokenType };

export type UserArrow = {
  type: 'user';
  color: 'green' | 'red' | 'yellow' | 'blue';
  from: SquareName;
  to: SquareName;
};

export type StockfishArrow = {
  type: 'stockfish';
  thickness: 'primary' | 'secondary' | 'reply';
  from: SquareName;
  to: SquareName;
};

export type Arrow = UserArrow | StockfishArrow;

export type Token = {
  type: TokenType;
  square: SquareName;
};

export type Valuation = {
  gauge: number; // [-1;1]
  centipawns?: number;
  mate?: number;
};

export const ChessBoard = memo(function ChessBoard({
  className,
  theme,

  orientation = 'white',
  position: fen,
  modal,
  notation,
  noAnimation = false,
  piecesAnimation = 'opacity',
  tokensAnimation = 'scale',
  noShadow = false,
  noAutoBorderRadius = false,
  noBorderRadius = false,
  noPieces = false,
  noWaypoints = false,

  interactive,
  highlightHovered,

  valuation,
  valuationValue,

  onPieceDrag,
  onSquareClick,
  onSquarePointerDown,
  onSquarePointerUp,
  onSquareOver,
  onSquareOut,
  onSquareDragOver,
  onSquareDrop,
  onDropOutside,
  onDragAbort,
  onSquarePlace,
  onTokenPlace,
  onTokenDrop,
  onTokenDropOutside,
  onKeyDown,

  isPremove,
  selectedFrom,
  selectedTo,
  premoveFrom,
  premoveTo,
  moves,

  arrows,
  onArrowDrag,
  onArrowPlace,

  tokens,

  winner,
  promotingFrom,
  promotingTo,
  onPromotion,

  onRotate,
}: Props) {
  const themeData = theme && BOARD_THEMES[theme];
  const piecesStyle = themeData?.pieces ?? DefaultChessPieces;
  const tokensStyle = themeData?.tokens ?? DefaultTokens;
  const themeClass = themeData?.className;

  const renderedRef = useIsRendered();

  const positions = useMemo(() => {
    if (!fen) return EMPTY_POSITIONS;
    const parsed = typeof fen === 'string' ? parseFen(fen)?.board : fen;
    return parsed ?? EMPTY_POSITIONS;
  }, [fen]);
  const positionsRef = useRef(positions);
  const pieceIdsRef = useRef<BoardPositionsIds | undefined>(undefined);
  const pieceIds = useMemo(
    () => trackIds(positionsRef.current, positions, pieceIdsRef.current),
    [positions],
  );
  positionsRef.current = positions;
  pieceIdsRef.current = pieceIds;

  const [dragInfo, setDragInfo] = useState<DragInfo>();
  const handleDragChange = useCallback((d?: DragInfo) => {
    if (!d) setDragHighlight(undefined);
    setDragInfo(d);
  }, []);
  const dragPreviewRef = useRef<HTMLDivElement | null>(null);

  const [arrowDragInfo, setArrowDragInfo] = useState<UserArrow>();
  const handleArrowDragChange = useCallback((a?: UserArrow) => {
    if (!a) setDragHighlight(undefined);
    setArrowDragInfo(a);
  }, []);
  const arrowPreviewRef = useRef<HTMLDivElement | null>(null);

  const [tokenDragInfo, setTokenDragInfo] = useState<TokenDragInfo>();
  const handleTokenDragChange = useCallback((t?: TokenDragInfo) => {
    if (!t) setDragHighlight(undefined);
    setTokenDragInfo(t);
  }, []);
  const tokenPreviewRef = useRef<HTMLDivElement | null>(null);

  // Фишка по клеткам — слою взаимодействий, чтобы знать, что тащить
  const tokensBySquare = useMemo(() => {
    const map: Partial<Record<SquareName, TokenType>> = {};
    tokens?.forEach((t) => {
      map[t.square] = t.type;
    });
    return map;
  }, [tokens]);

  const tokenPositionsRef = useRef(tokensBySquare);
  const tokenIdsRef = useRef<BoardPositionsIds | undefined>(undefined);
  const tokenIds = useMemo(
    () => trackIds(tokenPositionsRef.current, tokensBySquare, tokenIdsRef.current),
    [tokensBySquare],
  );
  tokenPositionsRef.current = tokensBySquare;
  tokenIdsRef.current = tokenIds;

  const [dragHighlight, setDragHighlight] = useState<SquareName>();
  const handleSquareDragOver = useCallback(
    (to: SquareName, positions: BoardPositions, e: globalThis.PointerEvent) => {
      setDragHighlight(to);
      onSquareDragOver?.(to, positions, e);
    },
    [onSquareDragOver],
  );
  const handleHighlightReset = useCallback(() => setDragHighlight(undefined), []);

  // ТОНКИЙ МОМЕНТ:
  // Чтобы побороть ошибки округления в Safari (особенно iOS) и Chrom под
  // Windows в FullHD при позиционировании элементов, необходимо сделать ширину
  // кратной 8px + желательно задавать координаты в пикселях. Если бы не Chome,
  // было бы достаточно иметь ширину, кратную 4px.
  //
  // За ширину доски отвечает класс .board, а для расчёта пикселей воспользуемся
  // ResizeObserver
  const refPlayfield = useResizeObserver<HTMLDivElement>(
    (entry) => {
      // не анимировать коректировку смещения
      let el = refPlayfield.current;
      el?.classList.add(styles.correction);
      requestAnimationFrame(() => {
        el?.classList.remove(styles.correction);
      });

      const width = entry.contentRect.width;

      el?.style.setProperty('--_square-size', `${width / 8}px`);

      // меняем скругление рамок для маленьких досок
      if (!noAutoBorderRadius && !noBorderRadius) {
        el?.parentElement?.style.setProperty('--_border-radius', width <= 430 ? '2px' : '8px');
      }

      // скрываем координаты с маленьких досок
      if (width < 250) {
        el?.parentElement?.style.setProperty('--_coords-display', 'none');
      } else {
        el?.parentElement?.style.removeProperty('--_coords-display');
      }
    },
    [noAutoBorderRadius, noBorderRadius],
  );

  const circlesOnly = useMemo(
    () => arrows?.filter((a): a is UserArrow => a.type === 'user' && a.from === a.to),
    [arrows],
  );
  const arrowsOnly = useMemo(() => arrows?.filter((a) => a.from !== a.to), [arrows]);

  return (
    <article
      className={cx(styles.board, themeClass, className, {
        [styles.has_gauge]: valuation === 'show',
        [styles.has_hidden_gauge]: valuation === 'hide',
        [styles.has_no_shadow]: noShadow,
        [styles.has_no_border]: noBorderRadius,
      })}
      data-orientation={orientation}
      onKeyDown={onKeyDown}
      tabIndex={onKeyDown ? 0 : undefined}
    >
      <div
        className={styles.playfield}
        onPointerLeave={() => setDragHighlight(undefined)}
        ref={refPlayfield}
      >
        {/* 1 */}
        <LayerHighlights
          isPremove={isPremove}
          selectedFrom={selectedFrom}
          selectedTo={selectedTo}
          premoveFrom={premoveFrom}
          premoveTo={premoveTo}
          dragging={Boolean(dragInfo)}
          moves={moves}
          positions={positions}
          dragOverSquare={dragHighlight}
          noAnimation={noAnimation}
          renderedRef={renderedRef}
          noWaypoints={noWaypoints}
        />
        {/* 2 */}
        <LayerCircles circles={circlesOnly} arrowDragInfo={arrowDragInfo} />
        {/* 3 */}
        <LayerTokens
          tokens={tokens}
          tokensBySquare={tokensBySquare}
          tokenIds={tokenIds}
          tokensStyle={tokensStyle}
          dragInfo={tokenDragInfo}
          dragRef={tokenPreviewRef}
          noAnimation={noAnimation}
          animation={tokensAnimation}
          renderedRef={renderedRef}
        />
        {/* 4 */}
        {!noPieces && (
          <LayerPieces
            pieceIds={pieceIds}
            promotingFrom={promotingFrom}
            promotingTo={promotingTo}
            promotingPremove={promotingFrom && isPremove}
            positions={positions}
            piecesStyle={piecesStyle}
            winner={winner}
            dragInfo={dragInfo}
            dragReverseGhosting={dragInfo && isPremove}
            dragRef={dragPreviewRef}
            noAnimation={noAnimation}
            animation={piecesAnimation}
            renderedRef={renderedRef}
          />
        )}
        {/* 5 */}
        <LayerArrows
          orientation={orientation}
          arrows={arrowsOnly}
          arrowDragInfo={arrowDragInfo}
          arrowRef={arrowPreviewRef}
        />
        {/* 6 */}
        <LayerNotation orientation={orientation} notation={notation} />
        {onRotate && (
          <RotateBoardButton
            className={styles.rotate}
            onClick={() => onRotate(orientation === 'black' ? 'white' : 'black')}
          />
        )}
        {/* 7 */}
        <LayerInteractions
          dragRef={dragPreviewRef}
          interactive={interactive}
          highlightHovered={highlightHovered}
          orientation={orientation}
          positions={positions}
          pieceIds={pieceIds}
          tokensBySquare={tokensBySquare}
          dragInfo={dragInfo}
          arrowDragInfo={arrowDragInfo}
          arrowRef={arrowPreviewRef}
          tokenDragInfo={tokenDragInfo}
          tokenDragRef={tokenPreviewRef}
          onSquareClick={onSquareClick}
          onSquarePointerDown={onSquarePointerDown}
          onSquarePointerUp={onSquarePointerUp}
          onSquareOver={onSquareOver}
          onSquareOut={onSquareOut}
          onPieceDrag={onPieceDrag}
          onSquareDragOver={handleSquareDragOver}
          onSquareDrop={onSquareDrop}
          onDropOutside={onDropOutside}
          onDragAbort={onDragAbort}
          onSquarePlace={onSquarePlace}
          onTokenPlace={onTokenPlace}
          onTokenDrop={onTokenDrop}
          onTokenDropOutside={onTokenDropOutside}
          onDragChange={handleDragChange}
          onTokenDragChange={handleTokenDragChange}
          onArrowDrag={onArrowDrag}
          onArrowPlace={onArrowPlace}
          onArrowDragChange={handleArrowDragChange}
          onHighlightReset={handleHighlightReset}
        />
        {/* 8 */}
        <LayerPromotion
          from={promotingFrom}
          to={promotingTo}
          positions={positions}
          piecesStyle={piecesStyle}
          orientation={orientation}
          onPromotion={onPromotion}
          renderedRef={renderedRef}
        />
        {/* 9 */}
        <LayerModals modal={modal} />
      </div>
      {valuation === 'show' && (
        <ValuationGauge orientation={orientation} valuation={valuationValue} />
      )}
    </article>
  );
});

/*
    ____,-------------------------------,____
    \   |             Градусник         |   /
    /___|-------------------------------|___\
*/

function ValuationGauge({
  valuation = { gauge: 0 },
  orientation,
}: {
  valuation?: Valuation;
  orientation: 'white' | 'black';
}) {
  let gauge = Math.min(Math.max(valuation.gauge, -1), 1);
  if (orientation === 'black') gauge *= -1;

  return (
    <div
      className={styles.valuation_gauge}
      style={
        {
          '--_gauge-value-f092f667': `${((-gauge + 1) * 100) / 2}%`,
        } as CSSProperties
      }
    >
      <i />
      <i />
      <i />
      <i />
      <i />
      <i />
      <i />
      <i />
      <i />
    </div>
  );
}

/*
    ____,-------------------------------,____
    \   |             Слои              |   /
    /___|-------------------------------|___\
*/

// ------------------------------------------
// 1. Notation Layer: Маркировка доски

const NOTATION_OUTSIDE_WIDTH = 426;

const LayerNotation = memo(function LayerNotation({
  orientation,
  notation = 'auto',
}: {
  orientation: Props['orientation'];
  notation: Props['notation'];
}) {
  const ref = useRef<HTMLElement>(null);
  const isWhite = orientation === 'white';

  const ranks = isWhite ? RANKS_WHITE : RANKS_BLACK;
  const files = isWhite ? FILES_WHITE : FILES_BLACK;

  useLayoutEffect(() => {
    if (notation !== 'auto') return;

    const node = ref.current;
    const parent = node?.parentElement;

    if (!parent) return;

    const check = () => {
      const autoNotation: Props['notation'] =
        parent.clientWidth < NOTATION_OUTSIDE_WIDTH ? 'inside' : 'outside';
      node.setAttribute('data-notation', autoNotation);
    };
    check();

    const observer = new ResizeObserver(check);
    observer.observe(parent);

    return () => {
      observer.unobserve(parent);
      observer.disconnect();
    };
  }, [notation]);

  if (notation === 'none') return null;

  return (
    <section translate="no" className={styles.notation} inert data-notation={notation} ref={ref}>
      <ul className={styles.ranks}>
        {ranks.map((r) => (
          <li key={r}>{r}</li>
        ))}
      </ul>

      <ul className={styles.files}>
        {files.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
    </section>
  );
});

// ------------------------------------------
// 2. Highlights Layer: Подсветка клеток

const LayerHighlights = memo(function LayerHighlights({
  isPremove,
  selectedFrom,
  selectedTo,
  premoveFrom,
  premoveTo,
  dragging,
  moves,
  positions,
  dragOverSquare,
  noAnimation,
  noWaypoints,
  renderedRef,
}: {
  isPremove?: Props['isPremove'];
  selectedFrom: Props['selectedFrom'];
  selectedTo: Props['selectedTo'];
  premoveFrom: Props['premoveFrom'];
  premoveTo: Props['premoveTo'];
  dragging: boolean;
  moves: Props['moves'];
  positions: BoardPositions;
  dragOverSquare?: SquareName;
  noAnimation?: boolean;
  noWaypoints?: boolean;
  renderedRef: MutableRefObject<boolean>;
}) {
  return (
    <section className={styles.highlights} inert>
      {/* Selections */}
      <AnimatedDynamicNodes>
        {selectedFrom &&
        selectedFrom !== premoveFrom /* -- не пересекается с premove */ &&
        selectedFrom !== premoveTo /* -- не пересекается с premove */ &&
        ((!isPremove && dragging) /* -- не премув (учитываем стрелки) */ ||
          selectedFrom !== dragOverSquare)
          ? {
              key: `from-${selectedFrom}`,
              initial: renderedRef.current,
              animation: noAnimation ? 'none' : 'half-opacity',
              className: styles.positioned,
              attributes: { 'data-square': selectedFrom },
              children: (
                <i className={selectedTo || dragging ? styles.indication : styles.selection} />
              ),
            }
          : undefined}
        {premoveFrom && (dragging /* -- не рисуем стрелочки */ || premoveFrom !== dragOverSquare)
          ? {
              key: `from-${premoveFrom}-pre`,
              initial: renderedRef.current,
              animation: noAnimation ? 'none' : 'half-opacity',
              className: styles.positioned,
              attributes: { 'data-square': premoveFrom },
              children: (
                <i
                  className={cx(
                    premoveTo || dragging ? styles.indication : styles.selection,
                    styles.premove,
                  )}
                />
              ),
            }
          : undefined}
        {selectedTo &&
        selectedTo !== premoveTo /* -- не пересекается с premove */ &&
        selectedTo !== premoveFrom /* -- не пересекается с premove */ &&
        ((!isPremove && dragging) /* -- не премув (учитываем стрелки) */ ||
          selectedTo !== dragOverSquare)
          ? {
              key: `to-${selectedTo}`,
              initial: renderedRef.current,
              animation: noAnimation ? 'none' : 'half-opacity',
              className: styles.positioned,
              attributes: { 'data-square': selectedTo },
              children: <i className={styles.indication} />,
            }
          : undefined}
        {premoveTo && (dragging /* -- не рисуем стрелочки */ || premoveTo !== dragOverSquare)
          ? {
              key: `to-${premoveTo}-pre`,
              initial: renderedRef.current,
              animation: noAnimation ? 'none' : 'half-opacity',
              className: styles.positioned,
              attributes: { 'data-square': premoveTo },
              children: <i className={cx(styles.indication, styles.premove)} />,
            }
          : undefined}
      </AnimatedDynamicNodes>

      {/* Waypoints */}
      {!noWaypoints && (
        <AnimatedDynamicNodes>
          {moves &&
            moves.map(
              (m) =>
                ({
                  key: m,
                  animation: noAnimation ? 'none' : 'half-opacity',
                  className: styles.positioned,
                  initial: renderedRef.current,
                  attributes: { 'data-square': m },
                  children: positions[m] ? (
                    m === dragOverSquare ? null : (
                      <IconScope
                        className={cx(styles.brackets, {
                          [styles.premove]: isPremove,
                        })}
                      />
                    )
                  ) : (
                    <i
                      className={cx(styles.dot, {
                        [styles.premove]: isPremove,
                      })}
                    />
                  ),
                }) satisfies NodeInfo,
            )}
        </AnimatedDynamicNodes>
      )}

      {/* Drag Over Indicator */}
      {dragOverSquare &&
        (!dragging /* -- рисуем стрелочки */ ||
          (!isPremove && dragOverSquare !== selectedFrom) ||
          (isPremove && dragOverSquare !== premoveFrom)) && (
          <div
            className={cx(styles.positioned, styles.squareDragHighlight, {
              [styles.premove]: dragging /* -- не рисуем стрелочки */ && isPremove,
            })}
            data-square={dragOverSquare}
          >
            <IconBorder />
          </div>
        )}
    </section>
  );
});

// ------------------------------------------
// 3. Circles Layer: Кружочки

const LayerCircles = memo(function LayerCircles({
  circles = [],
  arrowDragInfo,
}: {
  circles?: UserArrow[];
  arrowDragInfo?: UserArrow;
}) {
  const isCirclePreview = Boolean(arrowDragInfo && arrowDragInfo.from === arrowDragInfo.to);
  const nodes = useMemo<NodeInfo[]>(() => circles.map(mapCircle), [circles]);

  return (
    <section className={styles.circles} inert>
      {arrowDragInfo && isCirclePreview && (
        <div
          className={cx(styles.circlePreview, styles.circle)}
          data-square={arrowDragInfo.from}
          data-color={arrowDragInfo.color}
        >
          <IconCircle />
        </div>
      )}
      <AnimatedDynamicNodes>{nodes}</AnimatedDynamicNodes>
    </section>
  );
});

function mapCircle(a: UserArrow): NodeInfo {
  return {
    key: `${a.from}`,
    className: styles.circle,
    animation: 'half-opacity',
    attributes: {
      'data-square': a.from,
      'data-color': a.color,
    },
    children: <IconCircle />,
  };
}

// ------------------------------------------
// 4. Tokens Layer: Фишки головоломок

const LayerTokens = memo(function LayerTokens({
  tokens,
  tokensBySquare,
  tokenIds,
  tokensStyle,
  dragInfo,
  dragRef,
  noAnimation,
  animation,
  renderedRef,
}: {
  tokens?: Token[];
  tokensBySquare: Partial<Record<SquareName, TokenType>>;
  tokenIds: BoardPositionsIds;
  tokensStyle: TokenIcons;
  dragInfo?: TokenDragInfo;
  dragRef: MutableRefObject<HTMLDivElement | null>;
  noAnimation: boolean;
  animation: Props['tokensAnimation'];
  renderedRef: MutableRefObject<boolean>;
}) {
  // Только что брошенную фишку не везем по доске: она уже под курсором
  const prevDragTokenId = usePrev(dragInfo && tokenIds.squares[dragInfo.square]);

  // undefined — слоя нет вовсе; пустой массив оставляет слой ради анимации ухода фишек
  if (!tokens) return null;

  const DragComponent = dragInfo && tokensStyle[dragInfo.token];

  return (
    <section className={styles.tokens} inert>
      <AnimatedDynamicNodes>
        {Object.entries(tokenIds.ids).map(([id, s]) => {
          if (!s) return null;

          const type = tokensBySquare[s]!;
          const Component = tokensStyle[type];

          return {
            key: id,
            className: cx(styles.token, {
              [styles.ghost]: s === dragInfo?.square,
              [styles.instant]: noAnimation || id === prevDragTokenId,
            }),
            animation: noAnimation ? 'none' : animation,
            initial: renderedRef.current,
            attributes: { 'data-square': s, 'data-type': type },
            children: <Component />,
          } satisfies NodeInfo;
        })}
      </AnimatedDynamicNodes>

      {/* Drag Preview */}
      {dragInfo && DragComponent && (
        <div className={styles.tokenDragPreview} data-square={dragInfo.square} ref={dragRef} inert>
          <DragComponent />
        </div>
      )}
    </section>
  );
});

// ------------------------------------------
// 5. Pieces Layer: Шахматные фигуры

const LayerPieces = memo(function LayerPieces({
  pieceIds,
  promotingFrom,
  promotingTo,
  promotingPremove,
  positions,
  piecesStyle,
  winner,
  dragInfo,
  dragReverseGhosting,
  dragRef,
  noAnimation,
  animation,
  renderedRef,
}: {
  pieceIds: BoardPositionsIds;
  promotingFrom?: SquareName;
  promotingTo?: SquareName;
  promotingPremove?: boolean;
  positions: BoardPositions;
  piecesStyle: ChessPieces;
  winner: Props['winner'];
  dragInfo?: DragInfo;
  dragReverseGhosting?: boolean;
  dragRef: LegacyRef<HTMLDivElement>;
  noAnimation: boolean;
  animation: Props['piecesAnimation'];
  renderedRef: MutableRefObject<boolean>;
}) {
  const prevDragPieceId = usePrev(dragInfo?.pieceId);
  const prevPromotedFrom = usePrev(promotingFrom);

  const tippedKing: PieceCode | null =
    winner && winner !== 'draw' ? (winner === 'white' ? `bK` : 'wK') : null;

  // TODO: Добавить delay для "съеденых" фигур, чтобы кушающие фигуры успевали подъехать поближе
  return (
    <section className={styles.pieces} inert>
      {/* Pieces */}
      <AnimatedDynamicNodes>
        {Object.entries(pieceIds.ids).map(([id, s]) => {
          // Во время интерактивного превращения пешки, скрываем любые фигуры, которые эта пешка могла съесть
          // В премуве ничего прятать не надо — там нет анимаций фигур, только подсветка клеток
          const mustHidePiece = s === promotingTo && !promotingPremove;

          if (!s || mustHidePiece) {
            return null;
          }

          const pieceCode = positions[s]!;

          const isPromoting = pieceIds.promotions?.includes(id) || promotingFrom === s;
          const isAutopromotingViaDrop = isPromoting && id === prevDragPieceId;
          const isGhosted =
            (!dragReverseGhosting && s === dragInfo?.square) ||
            (promotingPremove && promotingFrom === s);

          return {
            key: id,
            className: cx({
              [styles.positioned]: true,
              [styles.piece]: true,
              [styles.tippedKing]: positions[s] === tippedKing,
              [styles.instant]: noAnimation || id === prevDragPieceId || prevPromotedFrom === s,
            }),
            initial: renderedRef.current,
            attributes: {
              'data-square': s === promotingFrom && !promotingPremove ? promotingTo : s,
            },
            animation: noAnimation ? 'none' : animation,
            children: (
              <Piece
                piece={pieceCode}
                promoted={isPromoting}
                skipPromotionAnimation={isAutopromotingViaDrop}
                ghosted={isGhosted}
                piecesStyle={piecesStyle}
              />
            ),
          } satisfies NodeInfo;
        })}
      </AnimatedDynamicNodes>

      {/* Drag Preview */}
      {dragInfo && (
        <div className={styles.dragPreview} data-square={dragInfo.square} ref={dragRef} inert>
          <Piece
            piece={dragInfo.pieceCode}
            ghosted={dragReverseGhosting}
            piecesStyle={piecesStyle}
          />
        </div>
      )}
    </section>
  );
});

const Piece = memo(function Piece({
  className,
  piece,
  promoted,
  skipPromotionAnimation,
  ghosted,
  piecesStyle,
}: {
  className?: string;
  piece: PieceCode;
  promoted?: boolean;
  skipPromotionAnimation?: boolean;
  ghosted?: boolean;
  piecesStyle: ChessPieces;
}) {
  const Component = piecesStyle[extractPieceName(piece)];
  const isWhite = extractColor(piece) === 'w';
  const classes = cx(className, {
    [styles.white]: isWhite,
    [styles.black]: !isWhite,
    [styles.ghost]: ghosted,
  });

  return promoted ? (
    <PromotionComponent
      className={classes}
      From={skipPromotionAnimation ? Component : piecesStyle.P}
      To={Component}
    />
  ) : (
    <Component className={classes} />
  );
});

// ------------------------------------------
// 6. Arrows Layer: Стрелочки

const LayerArrows = memo(function LayerArrows({
  orientation,
  arrows = [],
  arrowDragInfo,
  arrowRef,
}: {
  orientation: Props['orientation'];
  arrows?: Props['arrows'];
  arrowDragInfo?: UserArrow;
  arrowRef: MutableRefObject<HTMLDivElement | null>;
}) {
  const isPreviewArrow = arrowDragInfo && arrowDragInfo.from !== arrowDragInfo.to;

  const nodes = useMemo(() => arrows.map((a) => mapArrow(a, orientation)), [arrows, orientation]);

  return (
    <section className={styles.arrows} inert>
      {arrowDragInfo && isPreviewArrow && (
        <div
          className={cx(styles.arrowPreview, styles.arrow)}
          data-square={arrowDragInfo.from}
          data-color={arrowDragInfo.color}
          ref={arrowRef}
        >
          <BoardArrow color={arrowDragInfo.color} style={{ width: 0 }} />
        </div>
      )}
      <AnimatedDynamicNodes>{nodes}</AnimatedDynamicNodes>
    </section>
  );
});

function mapArrow(a: Arrow, orientation: Props['orientation']): NodeInfo {
  const key = `${a.type}-${a.from}-${a.to}`;

  return {
    key,
    className: styles.arrow,
    animation: 'half-opacity',
    attributes: { 'data-square': a.from },
    children: (
      <>
        {a.type === 'user' && <BoardArrow color={a.color} style={calcArrowStyle(a, orientation)} />}
        {a.type === 'stockfish' && (
          <BoardStockfishArrow type={a.thickness} style={calcArrowStyle(a, orientation)} />
        )}
      </>
    ),
  };
}

function calcArrowStyle(
  a: Arrow,
  orientation: Props['orientation'],
): { width: string; transform: string } {
  const from = squareToCoords(a.from, orientation);
  const to = squareToCoords(a.to, orientation);

  const dx = to.x - from.x;
  const dy = to.y - from.y;

  const style = {
    width: `${Math.hypot(dx, dy) * 100}%`,
    transform: `rotate(${calcArrowAngleDeg(dx, dy)}deg)`,
  };

  return style;
}

// ------------------------------------------
// 7. Interactions Layer: Регистрация и обработка действий пользователя

// FIXME: pieceIds, position и interactive можно передавать как ref — они
// используются только в коллбэках - за исключением признака selectable, который
// зависит от positions, но его можно попробовать перенести

const LayerInteractions = memo(function LayerInteractions({
  interactive = 'none',
  highlightHovered,
  orientation,
  positions,
  pieceIds,
  tokensBySquare,
  dragRef,
  dragInfo,
  arrowDragInfo,
  arrowRef,
  tokenDragInfo,
  tokenDragRef,
  onPieceDrag,
  onSquareClick,
  onSquarePointerDown,
  onSquarePointerUp,
  onSquareOver,
  onSquareOut,
  onSquareDrop,
  onDropOutside,
  onDragAbort,
  onSquarePlace,
  onTokenPlace,
  onTokenDrop,
  onTokenDropOutside,
  onSquareDragOver,
  onDragChange,
  onTokenDragChange,
  onArrowDrag,
  onArrowPlace,
  onArrowDragChange,
  onHighlightReset,
}: {
  interactive: Props['interactive'];
  highlightHovered: Props['highlightHovered'];
  orientation: Props['orientation'];
  positions: BoardPositions;
  pieceIds: BoardPositionsIds;
  tokensBySquare: Partial<Record<SquareName, TokenType>>;
  dragRef: MutableRefObject<HTMLDivElement | null>;
  dragInfo?: DragInfo;
  arrowDragInfo?: UserArrow;
  arrowRef: MutableRefObject<HTMLDivElement | null>;
  tokenDragInfo?: TokenDragInfo;
  tokenDragRef: MutableRefObject<HTMLDivElement | null>;
  onPieceDrag: Props['onPieceDrag'];
  onSquareClick: Props['onSquareClick'];
  onSquarePointerDown: Props['onSquarePointerDown'];
  onSquarePointerUp: Props['onSquarePointerUp'];
  onSquareOver: Props['onSquareOver'];
  onSquareOut: Props['onSquareOut'];
  onSquareDrop: Props['onSquareDrop'];
  onDropOutside: Props['onDropOutside'];
  onDragAbort: Props['onDragAbort'];
  onSquarePlace: Props['onSquarePlace'];
  onTokenPlace: Props['onTokenPlace'];
  onTokenDrop: Props['onTokenDrop'];
  onTokenDropOutside: Props['onTokenDropOutside'];
  onSquareDragOver: Props['onSquareDragOver'];
  onDragChange: (s?: DragInfo) => void;
  onTokenDragChange: (t?: TokenDragInfo) => void;
  onArrowDrag: Props['onArrowDrag'];
  onArrowPlace: Props['onArrowPlace'];
  onArrowDragChange: (a?: UserArrow) => void;
  onHighlightReset: () => void;
}) {
  const squares = orientation === 'white' ? SQUARES : SQUARES_REVERSE;

  const mustRender =
    onSquareClick ||
    onPieceDrag ||
    onSquarePointerDown ||
    onSquarePointerUp ||
    onSquareOver ||
    onSquareOut ||
    onArrowDrag ||
    onSquarePlace ||
    onTokenPlace ||
    onTokenDrop;

  const handlePointerLeave = useCallback(() => onArrowDragChange(undefined), [onArrowDragChange]);

  return (
    <section
      className={cx(styles.interactions, {
        [styles.dragging]: dragInfo || tokenDragInfo,
        [styles.arrowDrawing]: arrowDragInfo,
      })}
      onPointerLeave={handlePointerLeave}
    >
      {mustRender &&
        squares.map((s) => (
          <InteractionSquare
            key={s}
            dragRef={dragRef}
            arrowRef={arrowRef}
            tokenDragRef={tokenDragRef}
            square={s}
            positions={positions}
            pieceIds={pieceIds}
            token={tokensBySquare[s]}
            interactive={interactive}
            highlightHovered={highlightHovered}
            onPieceDrag={onPieceDrag}
            onSquareClick={onSquareClick}
            onSquarePointerDown={onSquarePointerDown}
            onSquarePointerUp={onSquarePointerUp}
            onSquareOver={onSquareOver}
            onSquareOut={onSquareOut}
            onSquareDrop={onSquareDrop}
            onDropOutside={onDropOutside}
            onDragAbort={onDragAbort}
            onSquarePlace={onSquarePlace}
            onTokenPlace={onTokenPlace}
            onTokenDrop={onTokenDrop}
            onTokenDropOutside={onTokenDropOutside}
            onSquareDragOver={onSquareDragOver}
            onDragChange={onDragChange}
            onTokenDragChange={onTokenDragChange}
            onArrowDrag={onArrowDrag}
            onArrowPlace={onArrowPlace}
            onArrowDragChange={onArrowDragChange}
            onHighlightReset={onHighlightReset}
          />
        ))}
    </section>
  );
});

const InteractionSquare = memo(function InteractionSquare({
  square,
  positions,
  pieceIds,
  token,
  interactive = 'none',
  highlightHovered,
  dragRef,
  arrowRef,
  tokenDragRef,
  onPieceDrag,
  onSquareClick,
  onSquarePointerDown,
  onSquarePointerUp,
  onSquareOver,
  onSquareOut,
  onSquareDrop,
  onDropOutside,
  onDragAbort,
  onSquarePlace,
  onTokenPlace,
  onTokenDrop,
  onTokenDropOutside,
  onSquareDragOver,
  onDragChange,
  onTokenDragChange,
  onArrowDrag,
  onArrowPlace,
  onArrowDragChange,
  onHighlightReset,
}: {
  square: SquareName;
  positions: BoardPositions;
  pieceIds: BoardPositionsIds;
  token?: TokenType;
  interactive: Props['interactive'];
  highlightHovered: Props['highlightHovered'];
  dragRef: MutableRefObject<HTMLDivElement | null>;
  arrowRef: MutableRefObject<HTMLDivElement | null>;
  tokenDragRef: MutableRefObject<HTMLDivElement | null>;
  onPieceDrag: Props['onPieceDrag'];
  onSquareClick: Props['onSquareClick'];
  onSquarePointerDown: Props['onSquarePointerDown'];
  onSquarePointerUp: Props['onSquarePointerUp'];
  onSquareOver: Props['onSquareOver'];
  onSquareOut: Props['onSquareOut'];
  onSquareDrop: Props['onSquareDrop'];
  onDropOutside: Props['onDropOutside'];
  onDragAbort: Props['onDragAbort'];
  onSquarePlace: Props['onSquarePlace'];
  onTokenPlace: Props['onTokenPlace'];
  onTokenDrop: Props['onTokenDrop'];
  onTokenDropOutside: Props['onTokenDropOutside'];
  onSquareDragOver: Props['onSquareDragOver'];
  onDragChange: (s?: DragInfo) => void;
  onTokenDragChange: (t?: TokenDragInfo) => void;
  onArrowDrag: Props['onArrowDrag'];
  onArrowPlace: Props['onArrowPlace'];
  onArrowDragChange: (a?: UserArrow) => void;
  onHighlightReset: () => void;
}) {
  const piece = positions[square];
  const color = piece && extractColor(piece);
  const pieceId = pieceIds.squares[square];

  const selectable =
    piece &&
    {
      all: true,
      none: false,
      white: color === 'w',
      black: color === 'b',
    }[interactive];

  // Фишка тащится с клетки без фигуры — фигура всегда в приоритете
  const tokenDraggable = Boolean(token && !piece && onTokenDrop);

  const ref = useRef<HTMLElement | null>(null);

  // ТОНКИЙ МОМЕНТ: трекер перетаскиваний в useDrag глобальный. Отказ в canDrag
  // одного хука сбрасывает его для всех хуков на элементе, поэтому фигуру и
  // фишку разводим уже в filterPointer: фигура — только с клетки с фигурой
  useDrag<DragMoveData | undefined>(ref, dragRef, {
    filterPointer(e) {
      return e.button === 0 && !e.ctrlKey && !e.altKey && !e.shiftKey && piece
        ? 'accept'
        : 'reject';
    },
    canDrag: (e) =>
      // ЛКМ + meta = рисование стрелочек
      !e.ctrlKey &&
      !e.altKey &&
      !e.shiftKey &&
      Boolean(selectable && onPieceDrag?.(square, positions)),
    onStart() {
      if (!piece || !pieceId) return;

      const ac = new AbortController();

      ref.current?.addEventListener(
        'pointerleave',
        () => {
          ctx.draggedFromSelf = true;
        },
        { once: true, signal: ac.signal },
      );

      onDragChange({ square, pieceCode: piece, pieceId });

      const ctx: DragMoveData = { type: 'move', square, ac, draggedFromSelf: false };
      return ctx;
    },
    onStop({ droppable, data }, e) {
      data?.ac.abort();

      if (!droppable) {
        // ТОНКИЙ МОМЕНТ: onDragChange вызывается либо тут, либо в onDrop.
        // Оставить вызов только тут нельзя — приведёт к двойному рендеру:
        // 1. от onSquareDragOver в onDrop (ниже)
        // 2. и от onDragChange в onStop (тут)
        onDragChange(undefined);

        if (data?.draggedFromSelf) {
          if (e.buttons === 3) {
            onDragAbort?.(square, positions);
          } else {
            onDropOutside?.(square, positions);
          }
        }
      }
    },
  });

  useDrag<DragTokenData | undefined>(ref, tokenDragRef, {
    filterPointer(e) {
      return e.button === 0 && !e.ctrlKey && !e.altKey && !e.shiftKey && tokenDraggable
        ? 'accept'
        : 'reject';
    },
    canDrag: (e) => !e.ctrlKey && !e.altKey && !e.shiftKey && tokenDraggable,
    onStart() {
      if (!token) return;

      const ac = new AbortController();

      ref.current?.addEventListener(
        'pointerleave',
        () => {
          ctx.draggedFromSelf = true;
        },
        { once: true, signal: ac.signal },
      );

      onTokenDragChange({ square, token });

      const ctx: DragTokenData = { type: 'move-token', square, token, ac, draggedFromSelf: false };
      return ctx;
    },
    onStop({ droppable, data }, e) {
      data?.ac.abort();

      if (!droppable) {
        onTokenDragChange(undefined);

        // Вытащили за доску (не отмена второй кнопкой) — фишку снимаем
        if (data?.draggedFromSelf && e.buttons !== 3) {
          onTokenDropOutside?.(square, positions);
        }
      }
    },
  });

  useDrag<DragArrowData>(ref, arrowRef, {
    // хотим начинать drag сразу по pointerDown (right mouse click) или ctrlKey
    filterPointer(e) {
      return e.button === 2 || e.ctrlKey || e.altKey || e.shiftKey ? 'force-drag' : 'reject';
    },
    customUpdate(_, e) {
      if (!arrowRef.current) return;

      const arrowEl = arrowRef.current.children[0] as HTMLElement;
      const squareRect = arrowRef.current.getBoundingClientRect();

      const dx = e.clientX - squareRect.left;
      const dy = squareRect.top + squareRect.height / 2 - e.clientY;

      Object.assign(arrowEl.style, {
        width: `${Math.hypot(dx, dy)}px`,
        transform: `rotate(${calcArrowAngleDeg(dx, dy)}deg)`,
      });
    },
    canDrag: (e) =>
      (e.ctrlKey || e.altKey || e.shiftKey || e.button === 2) && Boolean(onArrowDrag?.(square)),
    onStart(_, e) {
      let color: DragArrowData['color'] = 'green';
      if (e.altKey && e.ctrlKey) color = 'yellow';
      else if (e.ctrlKey) color = 'red';
      else if (e.altKey) color = 'blue';

      const ac = new AbortController();
      ref.current?.addEventListener(
        'pointerleave',
        () => {
          ctx.draggedFromSelf = true;
        },
        { once: true, signal: ac.signal },
      );

      onArrowDragChange({ type: 'user', color, from: square, to: square });

      const ctx: DragArrowData = { type: 'arrow', color, square, ac, draggedFromSelf: false };
      return ctx;
    },
    onStop(ctx, e) {
      ctx.data.ac.abort();

      if (!ctx.droppable) {
        if (!ctx.data.draggedFromSelf && e.buttons !== 3) {
          onArrowPlace?.({
            type: 'user',
            color: ctx.data.color,
            from: square,
            to: square,
          });
        }

        onArrowDragChange(undefined);
      }
    },
  });

  type DragData = DragMoveData | DragArrowData | DragPlaceData | DragTokenData;
  useDrop<DragData>(ref, {
    onDrop(ctx) {
      const to = square;

      if (ctx.data.type === 'arrow') {
        const from = ctx.data.square;
        onArrowDragChange(undefined);
        if (from) {
          onArrowPlace?.({ type: 'user', color: ctx.data.color, from, to });
        }
      } else if (ctx.data.type === 'move') {
        const from = ctx.data.square;
        if (from) onSquareDrop?.(from, to, positions);
        onDragChange(undefined);
      } else if (ctx.data.type === 'place') {
        onSquarePlace?.(to, ctx.data.piece, positions);
        onHighlightReset();
      } else if (ctx.data.type === 'place-token') {
        onTokenPlace?.(to, ctx.data.token, positions);
        onHighlightReset();
      } else if (ctx.data.type === 'move-token') {
        onTokenDrop?.(ctx.data.square, to, positions);
        onTokenDragChange(undefined);
      }
    },
    onEnter(ctx, e) {
      if (ctx.data.type === 'arrow') {
        const from = ctx.data.square;
        const to = square;
        onArrowDragChange({ type: 'user', color: ctx.data.color, from, to });
        from === to ? onHighlightReset() : onSquareDragOver?.(to, positions, e);
      } else {
        onSquareDragOver?.(square, positions, e);
      }
    },
  });

  return (
    <i
      ref={ref}
      className={cx(styles.square, { [styles.selectable]: selectable || tokenDraggable })}
      onPointerEnter={(e) => {
        // Подсветка под курсором — тем же путем, что при перетаскивании;
        // уход с доски сбрасывает ее в onPointerLeave игрового поля
        if (highlightHovered) onSquareDragOver?.(square, positions, e.nativeEvent);
        onSquareOver?.(square, positions);
      }}
      onPointerLeave={() => onSquareOut?.(square, positions)}
      onClick={(e) => onSquareClick?.(square, positions, e)}
      onPointerDown={(e) => {
        if (e.button === 0 && !e.ctrlKey) onArrowDragChange(undefined);
        onSquarePointerDown?.(square, positions, e);
      }}
      onPointerUp={(e) => onSquarePointerUp?.(square, positions, e)}
      onContextMenu={FUNC_PREVENT_DEFAULT}
    />
  );
});

// ------------------------------------------
// 8. Promotion Layer: Превращение пешки

const PROMOTION_REVERSED_AT = { white: '1', black: '8' };

const LayerPromotion = memo(function LayerPromotion({
  from,
  to,
  positions,
  piecesStyle,
  orientation,
  onPromotion,
  renderedRef,
}: {
  from?: SquareName;
  to?: SquareName;
  positions: BoardPositions;
  piecesStyle: ChessPieces;
  orientation: Props['orientation'];
  renderedRef: MutableRefObject<boolean>;
  onPromotion: Props['onPromotion'];
}) {
  const piece = from ? positions[from] : null;
  const color = piece && extractColor(piece);

  return (
    <section className={styles.promotions}>
      {to && piece && (
        <AnimatedNode
          animation="scale"
          initial={renderedRef.current}
          className={styles.promotion}
          style={getPromotionPosition(orientation, to)}
        >
          <PromotionSelector
            pieceClassName={color === 'w' ? styles.white : styles.black}
            piecesStyle={piecesStyle}
            reverse={PROMOTION_REVERSED_AT[orientation] === extractRank(to)}
            onSelect={(p) => onPromotion?.(p)}
          />
        </AnimatedNode>
      )}
    </section>
  );
});

function PromotionComponent({
  className,
  noDelay,
  From,
  To,
}: {
  className?: string;
  noDelay?: boolean;
  From: ChessPieces['P'];
  To: ChessPieces['P'];
}) {
  const [switched, setSwitched] = useState(noDelay);

  useEffect(() => {
    if (noDelay) return;

    const t = setTimeout(() => {
      setSwitched(true);
    }, 250);

    return () => clearTimeout(t);
  }, [noDelay]);

  // TODO: Заменить на обобщённый компонент <Сrossfade from={} to={} />
  // TODO: Синхронизировать DELAY c анимациями

  return switched ? <To className={className} /> : <From className={className} />;
}

// ------------------------------------------
// 9. Modals Layer: Диалоги поверх доски

const LayerModals = memo(function LayerModals({ modal }: { modal: Props['modal'] }) {
  return (
    <section className={styles.modal}>
      <AnimatedNode>{modal}</AnimatedNode>
    </section>
  );
});

/*
    ____,-------------------------------,____
    \   |            Иконки             |   /
    /___|-------------------------------|___\
*/

function IconScope({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 86 86"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.8589 2L21.25 2C22.3546 2 23.25 2.89544 23.25 4C23.25 5.10457 22.3546 6 21.25 6H16C13.115 6 11.1525 6.00425 9.68287 6.20184C8.27076 6.39169 7.61672 6.72644 7.17158 7.17158C6.72644 7.61672 6.39169 8.27076 6.20184 9.68287C6.00425 11.1525 6 13.115 6 16V21.5C6 22.6046 5.10457 23.5 4 23.5C2.89544 23.5 2 22.6046 2 21.5L2 15.8589C1.99993 13.1506 1.99987 10.9174 2.23751 9.14988C2.487 7.29422 3.03093 5.65537 4.34315 4.34315C5.65537 3.03093 7.29422 2.487 9.14988 2.23751C10.9174 1.99987 13.1506 1.99993 15.8589 2ZM75.3171 6.20184C73.8475 6.00425 71.885 6 69 6H63.75C62.6454 6 61.75 5.10457 61.75 4C61.75 2.89544 62.6454 2 63.75 2L69.1412 2C71.8494 1.99993 74.0826 1.99987 75.8501 2.23751C77.7058 2.487 79.3446 3.03093 80.6569 4.34315C81.9691 5.65537 82.513 7.29422 82.7625 9.14988C83.0001 10.9174 83.0001 13.1506 83 15.8588V21.5C83 22.6046 82.1046 23.5 81 23.5C79.8954 23.5 79 22.6046 79 21.5V16C79 13.115 78.9958 11.1525 78.7982 9.68287C78.6083 8.27076 78.2736 7.61672 77.8284 7.17158C77.3833 6.72644 76.7292 6.39169 75.3171 6.20184ZM4 62.5C5.10457 62.5 6 63.3954 6 64.5V70C6 72.885 6.00425 74.8475 6.20184 76.3171C6.39169 77.7292 6.72644 78.3833 7.17158 78.8284C7.61672 79.2736 8.27076 79.6083 9.68287 79.7982C11.1525 79.9958 13.115 80 16 80H21.25C22.3546 80 23.25 80.8954 23.25 82C23.25 83.1046 22.3546 84 21.25 84H15.8588C13.1506 84.0001 10.9174 84.0001 9.14988 83.7625C7.29422 83.513 5.65537 82.9691 4.34315 81.6569C3.03093 80.3446 2.487 78.7058 2.23751 76.8501C1.99987 75.0826 1.99993 72.8494 2 70.1412L2 64.5C2 63.3954 2.89544 62.5 4 62.5ZM79 70V64.5C79 63.3954 79.8954 62.5 81 62.5C82.1046 62.5 83 63.3954 83 64.5V70.1412C83.0001 72.8494 83.0001 75.0826 82.7625 76.8501C82.513 78.7058 81.9691 80.3446 80.6569 81.6569C79.3446 82.9691 77.7058 83.513 75.8501 83.7625C74.0826 84.0001 71.8494 84.0001 69.1412 84H63.75C62.6454 84 61.75 83.1046 61.75 82C61.75 80.8954 62.6454 80 63.75 80H69C71.885 80 73.8475 79.9958 75.3171 79.7982C76.7292 79.6083 77.3833 79.2736 77.8284 78.8284C78.2736 78.3833 78.6083 77.7292 78.7982 76.3171C78.9958 74.8475 79 72.885 79 70Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconBorder({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <svg
      className={className}
      style={style}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 82 82"
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.359 0H60.25v4H14.5c-2.885 0-4.847.004-6.317.202-1.412.19-2.066.524-2.511.97-.446.445-.78 1.099-.97 2.51C4.504 9.153 4.5 11.116 4.5 14v7.5h-4v-7.641c0-2.708 0-4.942.238-6.71.249-1.855.793-3.494 2.105-4.806C4.155 1.031 5.794.487 7.65.238 9.417 0 11.65 0 14.359 0Zm59.458 4.202C72.347 4.004 70.385 4 67.5 4h-7.25V0h7.391c2.708 0 4.942 0 6.71.238 1.855.249 3.494.793 4.806 2.105 1.312 1.312 1.856 2.951 2.106 4.807.237 1.767.237 4 .237 6.709V60.5h-4V14c0-2.885-.004-4.847-.202-6.317-.19-1.412-.524-2.066-.97-2.511-.445-.446-1.099-.78-2.51-.97ZM4.5 21.5V68c0 2.885.004 4.847.202 6.317.19 1.412.524 2.066.97 2.511.445.446 1.099.78 2.51.97 1.47.198 3.433.202 6.318.202h45.75v4H14.359c-2.708 0-4.942 0-6.71-.237-1.855-.25-3.494-.794-4.806-2.106C1.531 78.345.987 76.706.738 74.85.5 73.083.5 70.85.5 68.141V21.5h4Zm73 46.5v-7.5h4v7.641c0 2.708 0 4.942-.237 6.71-.25 1.855-.794 3.494-2.106 4.806-1.312 1.312-2.951 1.856-4.807 2.106-1.767.237-4 .237-6.709.237H60.25v-4h7.25c2.885 0 4.847-.004 6.317-.202 1.412-.19 2.066-.524 2.511-.97.446-.445.78-1.099.97-2.51.198-1.47.202-3.433.202-6.318Z"
      />
    </svg>
  );
}

function IconCircle() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 56 56">
      <circle cx="28" cy="28" r="26" stroke="currentColor" strokeWidth="2.8" />
    </svg>
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

function extractPieceName(piece: PieceCode): PieceName {
  return piece[1] as PieceName;
}

function extractRank(s: SquareName): RankName {
  return s[1] as RankName;
}

function extractColor(piece: PieceCode): Color {
  return piece[0] as Color;
}

function squareToCoords(
  s: SquareName,
  orientation: Props['orientation'],
): { x: number; y: number } {
  const ranks = orientation === 'white' ? '_abcdefgh' : '_hgfedcba';
  const files = orientation === 'white' ? '_12345678' : '_87654321';
  const x = ranks.indexOf(s[0]!);
  const y = files.indexOf(s[1]!);
  return { x, y };
}

function calcArrowAngleDeg(dx: number, dy: number) {
  const angleRad = Math.atan2(dx, dy);
  return radToDeg(angleRad) - /* начальное состояние стрелки */ 90;
}

function radToDeg(radians: number) {
  return (radians * 180) / Math.PI;
}
